import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  FormControl,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  FormEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  useCreateProductMedicationMutation,
  useGetMedicationsQuery,
} from "src/api/services/medicationsApi";
import { useGetProductQuery } from "src/api/services/productsApi";
import { Medication } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { MatchParam } from "src/constants/matches";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  onClose: () => void;
}

const AddProductMedicationModal = ({ onClose, open }: Props) => {
  const { productId } = useParams<MatchParam<"PRODUCT_ID">>();
  const [medication, setMedication] = useState<Medication | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [addProductMedication] = useCreateProductMedicationMutation();
  const errLog = useSentryLogger("error");

  const { data: product } = useGetProductQuery(
    productId ? productId : skipToken,
  );

  const treatment = useMemo(() => {
    if (product == null) return undefined;

    const lowercaseCategory = product.category.name.toLowerCase();

    return lowercaseCategory[0].toUpperCase() + lowercaseCategory.slice(1);
  }, [product]);

  const { data } = useGetMedicationsQuery(
    treatment ? { treatment, gender: product?.gender } : {},
  );

  const { medications } = useMemo(() => {
    if (data == null) return { medications: [], count: 0 };

    return { medications: data.data };
  }, [data]);

  const handleMedication = useCallback(
    (_: SyntheticEvent<Element | Event>, value: Medication | null) => {
      setMedication(value);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (medication == null || product == null) return;
      setLoading(true);
      try {
        await addProductMedication({
          product: product.slug,
          medication_id: medication.id,
          product_category_id: product.category.id,
        }).unwrap();
      } catch (error) {
        errLog(`Unable to add product medication to ${product.name}`, error);
      } finally {
        setMedication(null);
        setLoading(false);
        onClose();
      }
    },
    [addProductMedication, errLog, medication, onClose, product],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Medication
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <FormControl>
          <Autocomplete
            options={medications}
            getOptionLabel={(m) =>
              `${m.name} - ${m.dosage} (${m.pharmacy.name})`
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name} - {option.dosage} ({option.pharmacy.name})
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Medication" required multiline />
            )}
            value={medication}
            onChange={handleMedication}
          />
        </FormControl>
        <LoadingButton type="submit" loading={loading}>
          Add
        </LoadingButton>
      </Stack>
    </Modal>
  );
};

export default AddProductMedicationModal;
